/*
 * Time.
 *
 * Everything is stored as unix seconds UTC. Nothing is stored with an offset,
 * a zone, or a formatted string, because two different questions get asked of
 * the same instant and they have different right answers.
 *
 * ONE: "is this a night game?" Asked once, on the server, while the board is
 * being built. It must resolve in the FAMILY's zone, and it must resolve the
 * same way for everybody, or the board itself would differ depending on who
 * fetched it.
 *
 * TWO: "what time is this for me?" Asked on a phone, by one person, and it
 * must resolve in THAT DEVICE's zone. Grandpa in Arizona should read MST while
 * Scott in Utah reads MDT for the identical kickoff.
 *
 * The first is `isPrimetime`. The second is `formatKickoff`. Using either for
 * the other's job is a bug that produces plausible, wrong output.
 */

/**
 * Where the board is built, and the default for anything server-side.
 *
 * Overridden per-installation by `settings.family_tz`. This is the
 * commissioner's zone, not the viewer's, on purpose.
 */
export const FAMILY_TZ = 'America/Denver'

/** The hour, 0-23, at which a unix time falls in the given zone. */
export function localHour(unixSeconds: number, tz: string = FAMILY_TZ): number {
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    hourCycle: 'h23',
  })
  return Number(f.format(new Date(unixSeconds * 1000)))
}

/**
 * A night game: 6pm to 3am, in the family's zone.
 *
 * The upper bound wraps past midnight because a west-coast night game kicks at
 * 8:30pm Pacific, and a late one can still be running after midnight.
 *
 * This asks the family's zone rather than the server's. A Cloudflare Worker
 * runs in UTC, where a 1pm Mountain kickoff is hour 19 and would collect the
 * full primetime bonus, as would most of the Saturday afternoon slate - every
 * week, with no error, and with the weight quietly ceasing to mean anything.
 */
export function isPrimetime(unixSeconds: number, tz: string = FAMILY_TZ): boolean {
  const h = localHour(unixSeconds, tz)
  return h >= 18 || h < 3
}

/**
 * The zone this device is actually in, as an IANA name.
 *
 * Falls back to the family zone where `Intl` cannot answer - rare, but not
 * impossible on an old browser or a locked-down webview.
 */
export function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FAMILY_TZ
  } catch {
    return FAMILY_TZ
  }
}

function parts(unixSeconds: number, tz: string, opts: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, ...opts })
    .formatToParts(new Date(unixSeconds * 1000))
}

/**
 * The zone's short name AT THAT INSTANT - "MDT" in September, "MST" in
 * November.
 *
 * Derived per game rather than stored once, because the season crosses the
 * daylight-saving boundary on the first Sunday in November. A fixed "MT" would
 * be wrong for the last third of the schedule, and the games it would be wrong
 * for are the ones that decide the season.
 *
 * Arizona is the case that proves it matters: it never observes DST, so in
 * September it runs an hour behind Denver and in November it is the same time,
 * while reading "MST" throughout.
 */
export function zoneAbbrev(unixSeconds: number, tz: string): string {
  const p = parts(unixSeconds, tz, { timeZoneName: 'short' })
  return p.find((x) => x.type === 'timeZoneName')?.value ?? ''
}

/**
 * A kickoff as a person reads it: "Sat 1:00 PM MDT".
 *
 * Twelve-hour clock, no leading zero, day of week first - on a
 * Thursday-to-Monday board the day is the thing being scanned for. The zone is
 * always shown: with family across three time zones, an unlabelled "1:00 PM"
 * is an invitation to miss a kickoff.
 */
export function formatKickoff(unixSeconds: number, tz: string = deviceTimeZone()): string {
  const p = parts(unixSeconds, tz, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  })
  const get = (t: string) => p.find((x) => x.type === t)?.value ?? ''
  return `${get('weekday')} ${get('hour')}:${get('minute')} ${get('dayPeriod')} ${get('timeZoneName')}`
}

/**
 * "SAT SEP 5, 8:00 PM EDT" - the kickoff with its date.
 *
 * A board runs Thursday to Monday, so a weekday alone does identify a game
 * WITHIN one board. It stops being enough the moment anything else is on
 * screen beside it: the Add Game list reaches across a ten-day college week,
 * and two BYU fixtures a week apart both read "SAT" and were reported as one
 * game listed twice.
 *
 * The date is read in the same zone as the time, which is the viewer's. A
 * kickoff at half past midnight Eastern is the night before in Denver, and a
 * date taken from one zone beside a time taken from another is worse than no
 * date at all.
 */
export function kickoffWithDate(unixSeconds: number, tz: string = deviceTimeZone()): string {
  const d = parts(unixSeconds, tz, { month: 'short', day: 'numeric' })
  const get = (t: string) => d.find((x) => x.type === t)?.value ?? ''
  const when = formatKickoff(unixSeconds, tz)
  // formatKickoff leads with the weekday; the date slots in behind it.
  const [weekday, ...rest] = when.split(' ')
  return `${weekday} ${get('month')} ${get('day')}, ${rest.join(' ')}`.toUpperCase()
}

/**
 * The live badge's own words: "LIVE Q3 8:42", "LIVE HALF", or just "LIVE".
 *
 * Both halves of it are nullable and both are frequently null, so the rule is
 * that it degrades one step at a time and never invents anything: no period is
 * plain LIVE, which is exactly what the badge said before any of this existed.
 *
 * OVERTIME IS NOT A FIFTH QUARTER. ESPN counts periods straight on - 5, 6, 7 -
 * and both leagues get there, but neither calls it Q5. The NFL plays one
 * fifteen-minute overtime in the regular season and can play more than one in
 * January; college plays untimed possessions and keeps going all night. So
 * period 5 reads OT and everything above it counts the overtimes: 2OT, 3OT.
 * College overtime also has no clock at all, which the null already handles -
 * "OT" on its own is the whole truth about a college second overtime.
 *
 * HALFTIME IS A PERIOD WITH NO CLOCK. The adapter drops the clock the moment
 * play stops, because what ESPN keeps sending through a break is the last
 * value it had rather than a running one (shared/espn.ts has the two readings
 * that proved it). A stopped clock in the second period is the half - that is
 * what the second period ending IS - so it is named, rather than shown as a Q2
 * that has not moved in fifteen minutes. The end of the first and third
 * quarters lands here too, and reads as "Q1" and "Q3" with no clock: a
 * two-minute state that nobody is watching the badge through, and the quarter
 * shown is still the quarter the game is in.
 */
export function liveLabel(period: number | null, clock: string | null): string {
  if (period === null) return 'LIVE'
  if (period === 2 && clock === null) return 'LIVE HALF'
  const label = period <= 4 ? `Q${period}` : period === 5 ? 'OT' : `${period - 4}OT`
  return clock === null ? `LIVE ${label}` : `LIVE ${label} ${clock}`
}

/**
 * The board's window, as one sentence: "THU SEP 3 – MON SEP 7".
 *
 * NAMES THE LAST NIGHT OF FOOTBALL, not the exclusive bound. A window runs to
 * Tuesday 00:00, and printing "to Tue Sep 8" sent somebody looking for Tuesday
 * games that were never in it - while the same window on the next screen read
 * "Mon Sep 7". Two screens, one week, two answers, so it is written once here.
 *
 * COUNTED IN CALENDAR DAYS from the Thursday, not by subtracting 86400 from
 * the end. The first version did the latter and printed SUN NOV 1 for a window
 * ending Monday the 2nd: the clocks go back that morning, the day is 25 hours
 * long, and a fixed day's worth of seconds lands inside the day before.
 *
 * It is the sentence that says which games a board can contain at all, which
 * is the question behind every "why is that game not on the list".
 */
export function windowLabel(opensAt: number, closesAt: number, tz: string = FAMILY_TZ): string {
  const nights = Math.max(1, Math.round((closesAt - opensAt) / 86_400)) - 1
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric',
  }).formatToParts(new Date(opensAt * 1000))
  const n = (t: string) => Number(parts.find((p) => p.type === t)!.value)

  const fmt = (d: Date) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric',
    }).format(d).replace(',', '').toUpperCase()

  const first = new Date(Date.UTC(n('year'), n('month') - 1, n('day')))
  const last = new Date(Date.UTC(n('year'), n('month') - 1, n('day') + nights))
  return `${fmt(first)} \u2013 ${fmt(last)}`
}

/**
 * The build stamp: "87056CA · SEP 5, 5:12 PM".
 *
 * Answers "am I on the new one?" from inside the app. Until this existed the
 * only way to tell was to curl the asset hash off the served HTML, which is
 * not something anybody does from a phone in a stadium car park.
 *
 * The viewer's zone, like every other time on a screen - a build time in
 * Denver read by Grandpa in Arizona is a small lie, and this is the one string
 * whose entire job is to be believed.
 *
 * BOTH HALVES DEGRADE SEPARATELY. `__BUILD_ID__` is 'dev' outside a git
 * checkout and carries a trailing '+' when the tree was dirty at build time,
 * which is worth showing rather than hiding: it says somebody shipped
 * uncommitted work. An unparseable timestamp drops the date rather than
 * rendering "Invalid Date", and no id at all renders nothing, because a lone
 * timestamp identifies no build.
 */
export function buildStamp(
  id: string,
  builtAt: string,
  tz: string = deviceTimeZone(),
): string | null {
  if (!id) return null
  const label = id.toUpperCase()

  const ms = Date.parse(builtAt)
  if (Number.isNaN(ms)) return label

  const p = parts(Math.floor(ms / 1000), tz, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
  const get = (t: string) => p.find((x) => x.type === t)?.value ?? ''
  const when = `${get('month')} ${get('day')}, ${get('hour')}:${get('minute')} ${get('dayPeriod')}`
  return `${label} · ${when}`.toUpperCase()
}
